import React, { useEffect, useRef, useState } from "react";
import { Modal } from "./Modal";
import { Button } from "./primitives";

// Accessible, design-system confirm dialog built on the focus-trapping Modal — the replacement for
// the inaccessible/inconsistent native window.confirm(). Drive it imperatively from one state var:
//
//   const [confirm, setConfirm] = useState(null);
//   // where you used: if (!window.confirm("Delete X?")) return; doThing();
//   setConfirm({ title: "Delete X?", body: "This can't be undone.", danger: true,
//                confirmLabel: "Delete", onConfirm: () => doThing() });
//   // render once near the component root:
//   <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
//
// onConfirm may be synchronous or return a promise. The dialog owns the pending/error boundary:
// it cannot be dismissed or submitted twice while work is pending, closes only after success, and
// keeps the actionable server error visible when the operation rejects.
const actionErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object") {
    const message = detail.message || detail.detail;
    if (typeof message === "string" && message.trim()) return message;
  }
  if (typeof error?.message === "string" && error.message.trim()) return error.message;
  return "The action couldn't be completed. Review the record and try again.";
};

export function ConfirmDialog({ state, onClose }) {
  const open = !!state;
  const [pending, setPending] = useState(false);
  const [actionError, setActionError] = useState("");
  const [terminalError, setTerminalError] = useState(false);
  const submittingRef = useRef(false);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
    // Some legacy callers update the label object while their operation is running. That render
    // must not unlock the dialog; reset only when no submitted operation owns the boundary.
    if (!submittingRef.current) {
      setPending(false);
      setActionError("");
      setTerminalError(false);
    }
  }, [state]);

  const requestClose = () => {
    if (!submittingRef.current) onClose?.();
  };

  const confirm = async () => {
    if (submittingRef.current || !state?.onConfirm) return;
    const submittedState = state;
    submittingRef.current = true;
    setPending(true);
    setActionError("");
    try {
      await state.onConfirm();
      // Do not let a late result dismiss a different confirmation that a parent rendered while the
      // first promise was pending.
      if (stateRef.current === submittedState) onClose?.();
    } catch (error) {
      if (stateRef.current === submittedState) {
        setActionError(actionErrorMessage(error));
        setTerminalError(Boolean(error?.terminalAction));
      }
    } finally {
      submittingRef.current = false;
      setPending(false);
    }
  };

  const confirmLabel = state?.confirmLabel || (state?.danger ? "Delete" : "Confirm");
  return (
    <Modal
      open={open}
      onClose={requestClose}
      dismissible={!pending}
      title={state?.title || "Are you sure?"}
      size="sm"
      footer={terminalError ? (
        <Button variant="primary" onClick={requestClose} data-testid={state?.confirmTestId || "confirm-ok"}>
          Close
        </Button>
      ) : (
        <>
          <Button variant="ghost" onClick={requestClose} disabled={pending} data-testid="confirm-cancel">
            {state?.cancelLabel || "Cancel"}
          </Button>
          <Button
            variant={state?.danger ? "danger" : "primary"}
            onClick={confirm}
            loading={pending}
            aria-busy={pending || undefined}
            data-testid={state?.confirmTestId || "confirm-ok"}
          >
            {pending ? (state?.pendingLabel || `${confirmLabel}…`) : confirmLabel}
          </Button>
        </>
      )}
    >
      {typeof state?.body === "string"
        ? <p className="text-sm text-ink-2 whitespace-pre-line">{state.body}</p>
        : state?.body}
      {actionError && (
        <div
          role="alert"
          aria-live="assertive"
          className="mt-4 rounded-xl border border-critical/25 bg-critical-soft px-3.5 py-3 text-sm text-critical"
          data-testid="confirm-error"
        >
          {actionError}
        </div>
      )}
    </Modal>
  );
}
